import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [code, setCode] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const inputRefs = useRef([]);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    // Фокус на первый input при загрузке
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleCodeChange = (index, value) => {
    // Разрешаем только цифры
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Автоматический переход к следующему полю
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Удаление и переход к предыдущему полю
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Вставка из буфера обмена
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 4);
        if (digits.length === 4) {
          const newCode = digits.split('');
          setCode(newCode);
          inputRefs.current[3]?.focus();
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 4);
    if (digits.length === 4) {
      const newCode = digits.split('');
      setCode(newCode);
      inputRefs.current[3]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const codeString = code.join('');
    if (codeString.length !== 4) {
      setError('Введите 4-значный код');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/verify/email/verify/', { code: codeString });
      if (response.data.message) {
        await updateUser();
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Неверный код');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await api.post('/auth/verify/email/send/');
      setError('');
      // Показываем сообщение об успехе
      alert('Код отправлен на ваш email');
    } catch (error) {
      setError(error.response?.data?.error || 'Ошибка отправки кода');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-email-page">
      <Header />
      <div className="verify-email-container">
        <div className="verify-email-box">
          <div className="verify-email-header">
            <div className="verify-icon">✉️</div>
            <h2>Подтверждение email</h2>
          </div>
          
          <div className="verify-info">
            <div className="info-box">
              <span className="info-icon">ℹ️</span>
              <p>Мы отправили код подтверждения на <strong>{email}</strong></p>
            </div>
            <p className="verify-instructions">
              Введите 6-значный код из письма для завершения регистрации.
            </p>
          </div>

          <form onSubmit={handleVerify} className="verify-form">
            <div className="code-input-group">
              <label>Код подтверждения</label>
              <div className="code-inputs" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="code-input"
                    autoComplete="off"
                  />
                ))}
              </div>
              <p className="code-hint">Введите 4-значный код, отправленный на ваш email</p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="btn-verify" disabled={loading || code.join('').length !== 4}>
              {loading ? 'Проверка...' : '✓ Подтвердить email'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/register/student')}
              className="btn-back"
            >
              ← Вернуться к регистрации
            </button>

            <div className="resend-section">
              <p>Не получили код?</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="btn-resend"
              >
                {resending ? 'Отправка...' : 'Отправить повторно'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VerifyEmail;

